<template>
    <form class="rf-view login-view" @submit.prevent="next">
        <NavigationBar :title="$t('onboarding.contact.title')">
            <BackButton v-if="canPop" slot="left" @click="pop" />
        </NavigationBar>
        <main>
            <h1>
                {{ $t('onboarding.contact.title') }}
            </h1>
            <p>{{ $t('onboarding.contact.description') }}</p>

            <GeneralErrorsView :error-box="errorBox" />

            <InputBox :title="$t('fields.contactName')" :error-box="errorBox" error-fields="contactName">
                <input v-model="contactName" class="input" :placeholder="$t('fields.placeholders.contactName')" autocomplete="organization">
            </InputBox>

            <InputBox :title="$t('fields.contactPhone')" :error-box="errorBox" error-fields="contactPhone">
                <input v-model="contactPhone" class="input" :placeholder="$t('fields.placeholders.contactPhone')">
            </InputBox>
        </main>

        <FloatingFooter>
            <LoadingButton :loading="loading" class="block">
                <button class="button primary full">
                    {{ $t('buttons.signUp') }}
                </button>
            </LoadingButton>
        </FloatingFooter>
    </form>
</template>


<script lang="ts">
import { BackButton, FloatingFooter, InputBox, LoadingButton, NavigationBar } from "@restofrit/components"
import { Session } from "@restofrit/networking";
import { Language } from "@restofrit/structures";
import { CreateCustomer, Customer } from "@restofrit/structures";
import { ErrorBox, GeneralErrorsView } from "@simonbackx/simple-error-forms";
import { SimpleError, SimpleErrors } from "@simonbackx/simple-errors";
import { NavigationMixin } from "@simonbackx/vue-app-navigation";
import { Component, Mixins, Prop } from "vue-property-decorator";
import { isValidPhoneNumber } from 'libphonenumber-js'
import { Decoder } from "@simonbackx/simple-encoding";

@Component({
    components: {
        InputBox,
        GeneralErrorsView,
        LoadingButton,
        FloatingFooter,
        NavigationBar,
        BackButton
    },
})
export default class ContactPersonView extends Mixins(NavigationMixin) {
    @Prop({ required: true })
    customer: CreateCustomer

    @Prop({ required: true })
    callback: (newCustomer: Customer) => void

    errorBox: ErrorBox | null = null
    loading = false

    get contactName() {
        return this.customer.settings.contactName
    }

    set contactName(contactName: string) {
        this.customer.settings.contactName = contactName
    }

    get contactPhone() {
        return this.customer.settings.contactPhone
    }

    set contactPhone(contactPhone: string) {
        this.customer.settings.contactPhone = contactPhone
    }

    async next() {
        if (this.loading) {
            return
        }

        this.customer.settings.language = this.$i18n.locale.substr(0, 2) as Language

        // Create customer
        try {
            this.loading = true

            this.errorBox = null

            this.contactName = this.contactName.trim()
            this.contactPhone = this.contactPhone.trim()

            const errors = new SimpleErrors()
            if (this.contactName.length == 0) {
                errors.addError(new SimpleError({
                    code: "invalid_field",
                    message: "contactName is too short",
                    human: this.$t('errors.emptyContactName').toString(),
                    field: "contactName"
                }))
            }

            if (this.contactPhone.length == 0 || 
            !isValidPhoneNumber(this.contactPhone, "BE") || 
            !isValidPhoneNumber(this.contactPhone, "NL") || 
            !isValidPhoneNumber(this.contactPhone, "FR") ||
            !isValidPhoneNumber(this.contactPhone, "LU") ||
            !isValidPhoneNumber(this.contactPhone, "DE")) {
                errors.addError(new SimpleError({
                    code: "invalid_field",
                    message: "contactPhone is invalid",
                    human: this.$t('errors.invalidPhone').toString(),
                    field: "contactPhone"
                }))
            }

            errors.throwIfNotEmpty()

            const response = await Session.shared.authenticatedServer.request({
                method: "POST",
                path: "/customers/create-customer",
                body: this.customer.getId(),
                decoder: Customer as Decoder<Customer>
            })

            const newCustomer = response.data;

            // Rebuild component (needed so we request push notification permissions etc) + set active customer
            this.callback(newCustomer)
        } catch (e) {
            this.errorBox = new ErrorBox(e)
        }

        this.loading = false
        
    }
    
}
</script>